/**
 * Secure Access → Dashboard — the tenant overview (design 19 §7.4, VSNP-1221).
 *
 * The one page that answers "is my access platform healthy and who is using
 * it" without naming a gateway first. Every PromQL query here is written
 * without a tenant matcher: the proxy injects the caller's tenant and the node
 * allow-list (design §6.3), which is also why these panels are the cheapest
 * end-to-end test of that guard.
 *
 * Sessions and users come from `access_sessions` rather than from a metric, on
 * purpose — "who is connected" is a question about identities, and identities
 * are deliberately absent from every metric label (design §5.1).
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, AlertTriangle, Gauge, RefreshCw, Users, Wifi } from 'lucide-react';
import ZtnaChip from '../../components/access/ZtnaChip';
import StatCard from '../../components/common/StatCard';
import { QueryChart, QueryStat } from '../../components/observability/QueryPanels';
import { TimeRange } from '../../components/observability/TimeRange';
import accessObservabilityService, { type SessionsSummary } from '../../services/accessObservability.service';
import {
  DEFAULT_TIME_RANGE,
  avgOverRange,
  rangeDuration,
  rangeLabel,
  resolveTimeRange,
  type TimeRangeValue,
} from '../../utils/timeRange';

const EMPTY: SessionsSummary = {
  from: '', to: '',
  avgConcurrent: 0, peakConcurrent: 0,
  distinctUsers: 0, distinctDeviceOnly: 0,
  sessions: 0, bytesC2s: 0, bytesS2c: 0, bytesTotal: 0,
};

const AccessDashboard = () => {
  const [range, setRange] = useState<TimeRangeValue>(DEFAULT_TIME_RANGE);
  const [summary, setSummary] = useState<SessionsSummary>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Which metric cards' queries FAILED on their last load (VSNP-1436). A failed
  // query renders "—" on its card; this drives the one banner that says why,
  // the way the Gateways page reports a MetricsQueryError (VSNP-1265).
  const [failedStats, setFailedStats] = useState<Record<string, boolean>>({});
  const reportStat = useCallback((id: string, failed: boolean) => {
    setFailedStats((prev) => (prev[id] === failed ? prev : { ...prev, [id]: failed }));
  }, []);
  const failedCount = Object.values(failedStats).filter(Boolean).length;
  // Keyed remount: bumping this re-mounts the query panels, which is how a
  // page-level Refresh reaches components that own their own fetch.
  const [panelKey, setPanelKey] = useState(0);

  // Every panel on this page is scoped to the picker (design §9b). Gauges are
  // averaged over the range with a subquery and counters are totalled over it,
  // so no card can read 0 merely because the instant sample happens to be idle
  // — which is what a fixed 5-minute rate did on a 30-day view.
  const resolved = resolveTimeRange(range);
  const win = resolved.window;
  const total = rangeDuration(resolved);
  const label = rangeLabel(range);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { fromIso, toIso } = resolveTimeRange(range);
      // The server computes this over EVERY session overlapping the range.
      // Counting rows of a 500-row page here made the number shrink as a tenant
      // got busier (doc 22 §10.2).
      setSummary(
        await accessObservabilityService.getSessionsSummary(
          { from: fromIso, to: toIso },
          { showLoading: false },
        ),
      );
    } catch (err) {
      // The two session cards then show "—", not the zeros of EMPTY: a failed
      // read is not "nobody connected" (VSNP-1436).
      setError(err instanceof Error ? err.message : 'Failed to load the session summary');
      setSummary(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6" data-testid="access-dashboard">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-gray-900">Secure Access</h1>
            <ZtnaChip />
          </div>
          <p className="mt-1 text-gray-600">
            Who is connected, what they are reaching, and how the gateways serving you are holding up.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TimeRange value={range} onChange={setRange} />
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              void load();
              setPanelKey((k) => k + 1);
            }}
          >
            <RefreshCw className={`mr-2 inline h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">{error}</div>}

      {failedCount > 0 && (
        // Warning, not danger, and worded like the Gateways page's banner:
        // the session cards (database) may still be right; only the metric
        // cards are missing, and "—" there means "not measured", not zero.
        <div
          className="rounded-md border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-800"
          data-testid="dash-metrics-error"
          role="status"
        >
          Live metrics are unavailable, so {failedCount === 1 ? 'one metric card shows' : `${failedCount} metric cards show`}{' '}
          — rather than zero. The session counts come from the database and are not affected.
        </div>
      )}

      {/* Stat row (design §7.4) */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard
          title="Avg concurrent sessions"
          // The MEAN of the concurrency curve over the range, not a sample of
          // it: "open sessions" read at the instant the page loads is 0 at
          // 3 a.m. for a platform that carried hundreds of sessions that month.
          value={error ? '—' : summary.avgConcurrent}
          icon={Wifi}
          color="success"
          subtitle={error ? 'session summary unavailable' : `peak ${summary.peakConcurrent} · ${summary.sessions} in ${label}`}
          loading={loading}
          link="/access/activity"
        />
        <StatCard
          title="Active users"
          value={error ? '—' : summary.distinctUsers}
          icon={Users}
          color="primary"
          // Device-only sessions are reported NEXT TO the user count, never
          // inside it: they are devices the policy matched by certificate, and
          // folding them in would overstate how many people used the platform.
          subtitle={error ? 'session summary unavailable' : summary.distinctDeviceOnly > 0 ? `+${summary.distinctDeviceOnly} device-only` : 'IdP-verified'}
          loading={loading}
          link="/access/activity"
        />
        <QueryStat
          key={`flows-${panelKey}-${win}`}
          title="Decisions / s"
          query={`sum(rate(agw_flow_decisions_total[${win}]))`}
          // Same reason as Throughput: one decision every ten seconds is 0.10,
          // one every two minutes is 0.008 — and "0.01" for both would hide the
          // difference between a quiet gateway and a stopped one.
          // `null` reaches `format` only for an EMPTY answer; a failed query
          // renders "—" in QueryStat and never becomes "0" (VSNP-1436).
          format={(v) => (v === null ? '0' : v >= 0.01 ? v.toFixed(2) : v > 0 ? v.toFixed(4) : '0')}
          hint={`${win} rate`}
          testId="dash-stat-decisions"
          onStatus={reportStat}
        />
        <QueryStat
          key={`mbps-${panelKey}-${win}`}
          title="Throughput"
          query={`sum(rate(agw_flow_bytes_total[${win}])) * 8 / 1000000`}
          // Precision follows the magnitude. Two decimals is right for a busy
          // gateway and wrong for a quiet one: a lab tenant moving 4 kbps would
          // read "0.00 Mbps", which is the same thing a broken query says.
          format={(v) =>
            v === null ? '0 Mbps'
              : v >= 1 ? `${v.toFixed(2)} Mbps`
                : v >= 0.001 ? `${(v * 1000).toFixed(1)} kbps`
                  : v > 0 ? '<1 kbps'
                    : '0 Mbps'
          }
          hint={`${win} average`}
          testId="dash-stat-mbps"
          onStatus={reportStat}
        />
        <QueryStat
          key={`denies-${panelKey}-${total}`}
          title={`Denies (${label})`}
          // increase() over the WHOLE range, not a fixed 24 h: a card whose
          // window ignores the picker answers a different question from every
          // chart beside it (doc 22 §10.4).
          query={`sum(increase(agw_flow_decisions_total{action="deny"}[${total}]))`}
          format={(v) => (v === null ? '0' : String(Math.round(v)))}
          hint="flow decisions refused"
          testId="dash-stat-denies"
          onStatus={reportStat}
        />
        <QueryStat
          key={`unserved-${panelKey}-${total}`}
          title="Apps with no connector"
          // Averaged over the range as well: the series only exists WHILE an
          // app has no connector, so an instant read says nothing about an
          // outage that ended an hour ago.
          query={avgOverRange('count(agw_connector_app_unserved{node_kind="gateway"} == 1)', resolved)}
          format={(v) => (v === null ? '0' : v < 1 ? v.toFixed(2) : String(Math.round(v)))}
          hint={`average over ${label}`}
          testId="dash-stat-unserved"
          onStatus={reportStat}
        />
      </div>

      {/* Charts (design §7.4) */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <QueryChart
          key={`c1-${panelKey}`}
          title="Sessions over time"
          query="sum by (session_kind) (agw_sessions_live)"
          range={range}
          labelKeys={['session_kind']}
          testId="dash-chart-sessions"
        />
        <QueryChart
          key={`c2-${panelKey}`}
          title="Gateway usage split"
          query="sum by (node) (agw_sessions_live)"
          range={range}
          labelKeys={['node']}
          testId="dash-chart-gateways"
        />
        <QueryChart
          key={`c3-${panelKey}-${win}`}
          title="Top apps by throughput (B/s)"
          query={`topk(5, sum by (app) (rate(agw_flow_bytes_total[${win}])))`}
          range={range}
          labelKeys={['app']}
          unit=" B/s"
          testId="dash-chart-topapps"
        />
        <QueryChart
          key={`c4-${panelKey}-${win}`}
          title="Top apps by denies (per second)"
          query={`topk(5, sum by (app) (rate(agw_flow_decisions_total{action="deny"}[${win}])))`}
          range={range}
          labelKeys={['app']}
          testId="dash-chart-topdenies"
        />
        <QueryChart
          key={`c5-${panelKey}-${win}`}
          title="Decisions by reason (per second)"
          query={`sum by (reason) (rate(agw_flow_decisions_total[${win}]))`}
          range={range}
          labelKeys={['reason']}
          testId="dash-chart-reasons"
        />
        <div className="card flex flex-col justify-center gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Activity className="h-4 w-4 text-teal-600" /> Where to go next
          </h3>
          <Link to="/access/activity" className="text-sm text-primary-600 hover:underline">
            <Gauge className="mr-1 inline h-3.5 w-3.5" /> Activity — a user or device's sessions, apps and hosts
          </Link>
          <Link to="/access/security" className="text-sm text-primary-600 hover:underline">
            <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> Security — every deny, rejection and refusal, with export
          </Link>
          <Link to="/access/gateways" className="text-sm text-primary-600 hover:underline">
            <Wifi className="mr-1 inline h-3.5 w-3.5" /> Gateways — fleet health and load
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AccessDashboard;
